// The prologue: a childhood in six scenes, played once and alone before the town opens, invisible
// to every other player. The choices set where you come from, what you start with, what you are
// good at and what you want; the server works the effects out from these ids, never from numbers
// the browser sends. Every scene is a real Bamenda choice, not a quiz.
export const SCENES = [
  {
    id: 'born',
    title: 'You were born in',
    text: 'The year does not matter here. The place does.',
    choices: [
      { id: 'ntarikon', text: 'Ntarikon, in a single room by the stream. Your mother sold garri at Food Market.', home: 'room', start: 'quarter', money: 8000, skill: { hustle: 1 } },
      { id: 'nkwen', text: "Nkwen, in your father's compound behind the Nkwen market. Chickens, a maize farm, cousins everywhere.", home: 'selfcon', start: 'quarter', money: 25000, skill: { cooking: 1 } },
      { id: 'upstation', text: 'Up Station, in a flat with a view down over the whole city. Your father was a civil servant.', home: 'miniflat', start: 'quarter', money: 60000, skill: { charisma: 1 } },
      { id: 'bafut', text: "A village at Bafut. Your mother's farm fed you; the Fon's palace was a walk away.", home: 'room', start: 'quarter', money: 5000, skill: { fitness: 1 } },
      { id: 'abroad', text: 'Yaoundé, to parents who later left for Germany and sent you back to your grandmother in Bamenda.', home: 'selfcon', start: 'bushfaller', money: 120000, skill: { coding: 1 } },
    ],
  },
  {
    id: 'six',
    title: 'At six',
    text: 'Every child in Bamenda is raised by more than two people.',
    choices: [
      { id: 'granny', text: 'You were sent to the village to your grandmother. Water from the stream, church every Sunday, stories at night.', trait: 'clean', skill: { cooking: 1 } },
      { id: 'home', text: 'You stayed home and ran the quarter with the other children. Football in the dust until the light went.', trait: 'gym', skill: { fitness: 1 } },
      { id: 'douala', text: 'Your father went to Douala to find work and came home at Christmas. You learned early how money moves.', trait: 'hustler', skill: { hustle: 1 } },
      { id: 'aunt', text: "You lived with your aunt who ran a hair salon on Commercial Avenue. You could braid before you could spell.", trait: 'smooth', skill: { charisma: 1 } },
    ],
  },
  {
    id: 'school',
    title: 'Primary school',
    text: 'Government School, "free", with PTA fees of 15,000 FCFA a year that were not always there.',
    choices: [
      { id: 'paid', text: 'The fees were paid every September, somehow. You never missed a term.', mind: 1, skill: { coding: 1 } },
      { id: 'missed', text: 'Some years the fees came late and you stayed home a term. You caught up; you also learned to sell.', skill: { hustle: 1 }, money: 3000 },
      { id: 'puffpuff', text: 'You sold puff-puff from a tray after school to pay your own fees. Everybody in the quarter knew you.', skill: { cooking: 1, hustle: 1 }, money: 5000 },
      { id: 'closed', text: 'The crisis closed the school for two years. You learned at home from an uncle and from the radio.', skill: { music: 1 }, item: 'radio' },
    ],
  },
  {
    id: 'exam',
    title: 'The GCE year',
    text: 'Form Five. Everybody tells you this exam decides your life. It decides something.',
    choices: [
      { id: 'studied', text: 'You read under the kerosene lamp until the neighbours complained. Eight papers passed.', mind: 2, skill: { coding: 1 }, dream: 'voice' },
      { id: 'football', text: 'You played for the quarter team and passed four papers. Nobody in Nkwen forgets the final.', skill: { fitness: 2 }, dream: 'notable' },
      { id: 'phone', text: 'You fixed phones at the motor park on weekends and passed five. The park chairman knows your name.', skill: { hustle: 1, coding: 1 }, dream: 'chairman' },
      { id: 'choir', text: 'You led the church choir and passed six. The whole quarter heard your voice.', skill: { music: 2, charisma: 1 }, dream: 'voice' },
    ],
  },
  {
    id: 'after',
    title: 'After Form Five',
    text: 'The family sat down. Fees, or a trade, or the road.',
    choices: [
      { id: 'university', text: 'The University of Bamenda at Bambili: 50,000 a year, a hostel bed, two-minute noodles.', home: 'hostel', skill: { coding: 1, charisma: 1 }, money: -10000, dream: 'mogul' },
      { id: 'mechanic', text: 'Apprentice to a mechanic at Mile 4. Three years of grease and no pay, then your own tools.', skill: { hustle: 2 }, money: 10000, dream: 'chairman' },
      { id: 'salon', text: 'Apprentice at a salon on Commercial Avenue. You can do wigs, braids and gossip.', skill: { charisma: 2 }, money: 10000, dream: 'mogul' },
      { id: 'park', text: 'The motor park. Loading, calling passengers, counting coins. Nothing in Bamenda moves without you.', skill: { hustle: 2, fitness: 1 }, money: 15000, dream: 'chairman' },
      { id: 'kitchen', text: "Your mother's kitchen became a restaurant and you were the cook. Achu on Tuesdays, pepper soup at night.", trait: 'foodie', skill: { cooking: 2 }, money: 8000, dream: 'landlord' },
    ],
  },
  {
    id: 'blow',
    title: 'The year you turned twenty',
    text: 'Something happens to every family.',
    choices: [
      { id: 'funeral', text: "Your father died. The death celebration took the family's savings and a loan; the compound went to his successor, your elder brother.", money: -15000, trait: 'night' },
      { id: 'illness', text: 'Your mother fell ill and you carried the hospital bills for a year. She is better now.', money: -8000, skill: { cooking: 1 } },
      { id: 'njangi', text: "Your mother's njangi came through and she put the pot in your hand: start something.", money: 30000, trait: 'owambe' },
      { id: 'quiet', text: 'Nothing. A quiet year, rain on the zinc, school fees paid. You left for town with a bag and a bucket.', item: 'bucket', trait: 'lazy' },
    ],
  },
];

export const SCENE_IDS = SCENES.map((s) => s.id);

// Checks a set of choices from the browser: every scene answered with a real choice id.
export function validOrigin(origin) {
  if (!origin || typeof origin !== 'object') return false;
  for (const s of SCENES) if (!s.choices.some((c) => c.id === origin[s.id])) return false;
  return true;
}

// The effects of a set of choices, worked out from the content: start, home, money, up to two
// traits (the first two picked), skill levels (capped at 3), a dream (the last one picked), items.
export function originEffects(origin) {
  const out = { start: 'quarter', home: 'room', money: 0, traits: [], skills: {}, dream: null, items: [], mind: 0 };
  for (const s of SCENES) {
    const c = s.choices.find((k) => k.id === origin[s.id]);
    if (!c) continue;
    if (c.start) out.start = c.start;
    if (c.home) out.home = c.home;
    if (c.money) out.money += c.money;
    if (c.trait && out.traits.length < 2 && !out.traits.includes(c.trait)) out.traits.push(c.trait);
    if (c.skill) for (const [k, v] of Object.entries(c.skill)) out.skills[k] = Math.min(3, (out.skills[k] || 0) + v);
    if (c.dream) out.dream = c.dream;
    if (c.item && !out.items.includes(c.item)) out.items.push(c.item);
    if (c.mind) out.mind += c.mind;
  }
  out.money = Math.max(2000, out.money);
  return out;
}
